/**
 * Утилиты для работы с URL и query параметрами
 */

/**
 * Строит URL с query параметрами из объекта
 * Автоматически пропускает undefined значения
 *
 * @param baseUrl - базовый URL
 * @param params - объект с параметрами
 * @returns URL с query параметрами или базовый URL, если параметров нет
 *
 * @example
 * buildUrlWithParams('/api/reviews', { page: 1, pageSize: 10 })
 * // => '/api/reviews?page=1&pageSize=10'
 *
 * buildUrlWithParams('/api/reviews', { page: undefined })
 * // => '/api/reviews'
 */
export function buildUrlWithParams<T extends Record<string, unknown>>(
  baseUrl: string,
  params?: T,
): string {
  if (!params) {
    return baseUrl;
  }

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    // Пропускаем undefined значения
    if (value !== undefined) {
      searchParams.set(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/**
 * Создает объект query параметров для пагинации и сортировки
 *
 * @param params - параметры запроса
 * @returns объект с query параметрами (без undefined значений)
 */
export function buildQueryParams(params: {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  [key: string]: string | number | boolean | undefined;
}): Record<string, string | number | boolean> {
  const result: Record<string, string | number | boolean> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      result[key] = value;
    }
  });

  return result;
}
